import React, { useEffect, useRef } from "react";
import { M } from "./tokens";
import { useBrand, money } from "./BrandContext";

/* ══════════════════════════════════════════════════════════════
   THE OFFER CORE
   One choice, made big. The KPI is the headline, because the KPI
   is what every machine below gets graded on. No continue buttons.
   ══════════════════════════════════════════════════════════════ */

const MODES = {
  ltv: {
    id: "ltv",
    label: "A · they come back",
    accent: "#A78BFA",
    title: "My customers reorder.",
    sub: "Subscriptions, refills, a list that buys again. You can pay more for a customer when you know when you get it back.",
    tag: "payback period",
    kpi: "Payback period",
    verdict: "Everything below is graded on payback period.",
    levers: [
      ["Subscription at checkout", "Turn a one off into a term."],
      ["One click term extension", "Another three months, buyer only price."],
      ["Faster order two", "Shorten the gap, shorten the payback."],
    ],
    generic: [
      ["Live CPA", "read daily, not monthly"],
      ["Order one margin", "what the first order actually leaves"],
      ["Payback window", "30, 60 or 90 days, your call"],
      ["Target CPA", "the number that funds the window"],
    ],
  },
  aov: {
    id: "aov",
    label: "B · one big order",
    accent: "#FFC46B",
    title: "My money is in the first order.",
    sub: "One purchase, made as big as you honestly can. Day one pays for itself, so the work is bundles and upsells.",
    tag: "MER and order value",
    kpi: "MER and order value",
    verdict: "Everything below is graded on MER and order value.",
    levers: [
      ["Three tier bundle ladder", "Popcorn pricing, tested over time."],
      ["Middle tier overweighted", "The low anchor is there to lose."],
      ["Post purchase upsell", "Same order, one more click."],
    ],
    generic: [
      ["Blended MER", "the floor it will not break"],
      ["Order value", "the number it works on all week"],
      ["Bundle ladder", "three tiers, tested, not guessed"],
      ["Second bite", "post purchase, no extra CPA"],
    ],
  },
};

/* small drawn figures so the choice reads as a picture, not two big words */
function PaybackFigure({ accent, on }) {
  const c = accent;
  return (
    <svg viewBox="0 0 240 100" className="h-[118px] w-full" style={{ opacity: on ? 1 : 0.72 }} role="img" aria-label="Cumulative margin crossing acquisition cost around day sixty">
      <defs>
        <linearGradient id="pbFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.28" />
          <stop offset="100%" stopColor={c} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="0" y1="46" x2="240" y2="46" stroke={c} strokeOpacity="0.45" strokeWidth="1" strokeDasharray="4 4" />
      <text x="2" y="40" fill={c} fillOpacity="0.75" fontSize="8" letterSpacing="1.4">WHAT A CUSTOMER COST</text>
      <path d="M0 94 L48 74 L96 58 L132 46 L180 34 L240 26 L240 100 L0 100 Z" fill="url(#pbFill)" />
      <polyline points="0,94 48,74 96,58 132,46 180,34 240,26" fill="none" stroke={c} strokeWidth="2" strokeLinejoin="round" />
      <circle cx="132" cy="46" r="4" fill={c} />
      <text x="118" y="66" fill={c} fontSize="8.5" letterSpacing="1.2">DAY 60</text>
    </svg>
  );
}

function LadderFigure({ accent, on }) {
  const c = accent;
  const bars = [
    { x: 26, h: 30, label: "S" },
    { x: 100, h: 66, label: "M" },
    { x: 174, h: 46, label: "L" },
  ];
  return (
    <svg viewBox="0 0 240 100" className="h-[118px] w-full" style={{ opacity: on ? 1 : 0.72 }} role="img" aria-label="Three price tiers with the middle tier overweighted">
      <line x1="8" y1="82" x2="232" y2="82" stroke={c} strokeOpacity="0.3" strokeWidth="1" />
      {bars.map((b, i) => (
        <g key={b.label}>
          <rect
            x={b.x}
            y={82 - b.h}
            width="40"
            height={b.h}
            rx="4"
            fill={c}
            fillOpacity={i === 1 ? 0.55 : 0.16}
            stroke={c}
            strokeOpacity={i === 1 ? 0.9 : 0.35}
          />
          <text x={b.x + 20} y="95" fill={c} fillOpacity="0.8" fontSize="8.5" textAnchor="middle" letterSpacing="1.2">
            {b.label}
          </text>
        </g>
      ))}
      <text x="100" y="12" fill={c} fillOpacity="0.85" fontSize="8.5" letterSpacing="1.2">MOST ORDERS HERE</text>
    </svg>
  );
}

export default function OfferCore() {
  const { offerMode, setOfferMode, hasNumbers, n } = useBrand();
  const outRef = useRef(null);
  const mode = offerMode ? MODES[offerMode] : null;

  /* picking a side moves you on. No button to hunt for. */
  useEffect(() => {
    if (!offerMode || !outRef.current) return;
    const id = setTimeout(() => {
      outRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 260);
    return () => clearTimeout(id);
  }, [offerMode]);

  const stats =
    hasNumbers && offerMode === "ltv"
      ? [
          [money(n.cpa), "your CPA today"],
          [money(n.firstOrderMargin), "margin on order one"],
          [n.paybackDays === 0 ? "order one" : `${n.paybackDays} days`, "payback today"],
          [money(n.targetCpa60), "CPA for a 60 day payback"],
        ]
      : hasNumbers && offerMode === "aov"
      ? [
          [`${n.mer.toFixed(1)}x`, "blended MER today"],
          [money(n.aov), "order value today"],
          [money(n.targetAov), "order value after the ladder"],
          [`${money(n.aovUpside)}/mo`, "same traffic, bigger baskets"],
        ]
      : mode
      ? mode.generic
      : [];

  return (
    <section className="border-y" style={{ borderColor: M.border, background: M.deep }} id="offer-core" data-snap data-testid="offer-core">
      <div className="mx-auto w-full max-w-[1280px] px-5 py-12 sm:px-8 sm:py-24">
        <div className="f-mono mb-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: M.ok }}>
          One question first
        </div>
        <h2 className="f-grotesk max-w-[22ch] text-[28px] font-bold leading-[1.03] tracking-[-0.03em] sm:text-[54px]">
          Where do you actually make your money?
        </h2>
        <p className="mt-5 max-w-[56ch] text-[15px] leading-[1.8]" style={{ color: M.muted }}>
          Pick the one that sounds like your business. It sets the number every machine below is graded on.
        </p>

        {/* the fork, made big */}
        <div className="mt-8 grid gap-4 lg:grid-cols-2" data-testid="offer-fork">
          {Object.values(MODES).map((m) => {
            const on = offerMode === m.id;
            const off = offerMode && !on;
            const Figure = m.id === "ltv" ? PaybackFigure : LadderFigure;
            return (
              <button
                key={m.id}
                onClick={() => setOfferMode(m.id)}
                data-testid={`offer-mode-${m.id}`}
                className="focus-ring group rounded-3xl border p-6 text-left transition-transform hover:-translate-y-[3px] active:translate-y-0 sm:p-8"
                style={{
                  borderColor: on ? m.accent : M.border,
                  background: on ? `${m.accent}12` : M.surface,
                  boxShadow: on ? `0 30px 90px ${m.accent}22` : "none",
                  opacity: off ? 0.5 : 1,
                }}
                aria-pressed={on}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: m.accent, boxShadow: on ? `0 0 12px ${m.accent}` : "none", opacity: on ? 1 : 0.5 }}
                  />
                  <span className="f-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: on ? m.accent : M.dim }}>
                    {m.label}
                  </span>
                  <span
                    className="f-mono ml-auto rounded px-2.5 py-1 text-[9px] uppercase tracking-[0.12em]"
                    style={{
                      color: on ? m.accent : M.dim,
                      border: `1px solid ${on ? `${m.accent}66` : M.border}`,
                    }}
                  >
                    {on ? "selected" : "this is me"}
                  </span>
                </div>

                <div className="f-grotesk mt-4 text-[22px] font-bold leading-[1.06] tracking-[-0.03em] sm:mt-5 sm:text-[33px]">
                  {m.title}
                </div>

                <p className="mt-2.5 max-w-[44ch] text-[13px] leading-[1.7] sm:mt-3 sm:text-[14px]" style={{ color: M.muted }}>
                  {m.sub}
                </p>

                <div className="mt-5 hidden rounded-2xl border p-3 sm:block" style={{ borderColor: M.border, background: M.surface2 }}>
                  <Figure accent={m.accent} on={on} />
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <span className="f-mono text-[9px] uppercase tracking-[0.16em]" style={{ color: M.dim }}>
                    So the number is
                  </span>
                  <span
                    className="f-mono rounded px-2.5 py-1 text-[9.5px] uppercase tracking-[0.12em]"
                    style={{ color: m.accent, border: `1px solid ${m.accent}55`, background: `${m.accent}10` }}
                  >
                    {m.tag}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {!offerMode && (
          <p className="f-mono mt-6 text-[10px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>
            Tap one. You can switch any time.
          </p>
        )}

        {/* the consequence, short */}
        <div ref={outRef} className="scroll-mt-24">
          {mode && (
            <div className="mt-10" data-testid="offer-outcome" style={{ animation: "sixIn 480ms cubic-bezier(0.16,1,0.3,1) both" }}>
              <div className="f-mono mb-3 text-[9.5px] uppercase tracking-[0.18em]" style={{ color: mode.accent }}>
                Locked · {mode.kpi}
              </div>
              <h3 className="f-grotesk max-w-[26ch] text-[21px] font-bold leading-[1.08] tracking-[-0.025em] sm:text-[36px]">
                {mode.verdict}
              </h3>

              <div className="mt-6 grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4" data-testid="offer-readout">
                {stats.map(([v, l]) => (
                  <div key={l} className="rounded-2xl border p-3.5 sm:p-5" style={{ borderColor: M.border, background: M.surface }}>
                    <div className="f-grotesk tnum text-[19px] font-bold leading-none sm:text-[26px]" style={{ color: mode.accent }}>{v}</div>
                    <div className="f-mono mt-2 text-[8.5px] uppercase leading-relaxed tracking-[0.12em] sm:text-[9px]" style={{ color: M.muted }}>{l}</div>
                  </div>
                ))}
              </div>

              <div className="mt-7 grid grid-cols-1 gap-3 sm:mt-8 sm:grid-cols-3" data-testid="offer-levers">
                {mode.levers.map(([h, b]) => (
                  <div key={h} className="border-t pt-4" style={{ borderColor: mode.accent }}>
                    <div className="f-grotesk text-[15px] font-semibold">{h}</div>
                    <div className="mt-1 text-[13px] leading-[1.6]" style={{ color: M.muted }}>{b}</div>
                  </div>
                ))}
              </div>

              <p className="f-mono mt-9 text-[10px] uppercase tracking-[0.16em]" style={{ color: M.dim }}>
                Keep scrolling. Six machines, one after the other.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
